import { useParams, Link } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Star, MapPin, Phone, Mail, Globe, Heart, ArrowLeft, Check, Loader2 } from 'lucide-react';
import { useVendorBySlug } from '@/hooks/useVendor';
import { VENDOR_CATEGORIES, PRICE_RANGE_LABELS } from '@/types/vendor';

const VendorDetail = () => {
  const { id: slug } = useParams();
  const { data: vendor, isLoading } = useVendorBySlug(slug);

  if (isLoading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  if (!vendor) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-20 text-center">
          <h1 className="font-serif text-3xl text-foreground mb-4">Proveedor no encontrado</h1>
          <Link to="/vendors">
            <Button>Volver a proveedores</Button>
          </Link>
        </div>
      </Layout>
    );
  }

  const category = VENDOR_CATEGORIES.find(c => c.id === vendor.category);

  return (
    <Layout>
      <div className="bg-background min-h-screen">
        {/* Hero Image */}
        <div className="relative h-[50vh] overflow-hidden">
          <img
            src={vendor.image || '/vendor-placeholder.svg'}
            alt={vendor.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/20 to-transparent" />
          
          <div className="absolute top-6 left-6">
            <Link to="/vendors">
              <Button variant="secondary" size="sm" className="gap-2">
                <ArrowLeft className="w-4 h-4" />
                Volver
              </Button>
            </Link>
          </div>
        </div>

        <div className="container mx-auto px-4 -mt-32 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <div className="bg-card rounded-2xl p-8 shadow-card border border-border mb-8">
                <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <span className="bg-blush-light text-primary text-sm font-medium px-3 py-1 rounded-full">
                        {category?.icon} {category?.name}
                      </span>
                      <span className="bg-accent/20 text-accent-foreground text-sm font-bold px-3 py-1 rounded-full">
                        {PRICE_RANGE_LABELS[vendor.priceRange]}
                      </span>
                    </div>
                    <h1 className="font-serif text-3xl md:text-4xl font-semibold text-foreground">
                      {vendor.name}
                    </h1>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1 bg-gold-light px-4 py-2 rounded-xl">
                      <Star className="w-5 h-5 text-accent fill-current" />
                      <span className="font-bold text-accent">{vendor.rating.toFixed(1)}</span>
                      <span className="text-muted-foreground">({vendor.reviewCount} reseñas)</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center text-muted-foreground mb-6">
                  <MapPin className="w-5 h-5 mr-2" />
                  {vendor.location || 'España'}
                </div>

                <p className="text-foreground leading-relaxed mb-8">
                  {vendor.description || vendor.shortDescription}
                </p>

                {/* Services */}
                {vendor.services.length > 0 && (
                  <div>
                    <h3 className="font-serif text-xl font-semibold text-foreground mb-4">
                      Servicios incluidos
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {vendor.services.map((service, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-3 bg-sage-light/50 rounded-lg px-4 py-3"
                        >
                          <Check className="w-5 h-5 text-sage" />
                          <span className="text-foreground">{service}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Gallery */}
              {vendor.gallery.length > 0 && (
                <div className="bg-card rounded-2xl p-8 shadow-card border border-border">
                  <h3 className="font-serif text-xl font-semibold text-foreground mb-4">
                    Galería
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {vendor.gallery.map((image, index) => (
                      <div key={index} className="aspect-square rounded-xl overflow-hidden">
                        <img
                          src={image}
                          alt={`${vendor.name} - Imagen ${index + 1}`}
                          className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-card rounded-2xl p-6 shadow-card border border-border sticky top-24">
                <h3 className="font-serif text-xl font-semibold text-foreground mb-6">
                  Contactar
                </h3>

                <div className="space-y-4 mb-6">
                  {vendor.contactPhone && (
                    <a
                      href={`tel:${vendor.contactPhone}`}
                      className="flex items-center gap-3 p-4 rounded-xl bg-muted/50 hover:bg-muted transition-colors"
                    >
                      <Phone className="w-5 h-5 text-primary" />
                      <span className="text-foreground">{vendor.contactPhone}</span>
                    </a>
                  )}
                  
                  {vendor.contactEmail && (
                    <a
                      href={`mailto:${vendor.contactEmail}`}
                      className="flex items-center gap-3 p-4 rounded-xl bg-muted/50 hover:bg-muted transition-colors"
                    >
                      <Mail className="w-5 h-5 text-primary" />
                      <span className="text-foreground text-sm">{vendor.contactEmail}</span>
                    </a>
                  )}

                  {vendor.website && (
                    <a
                      href={vendor.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 p-4 rounded-xl bg-muted/50 hover:bg-muted transition-colors"
                    >
                      <Globe className="w-5 h-5 text-primary" />
                      <span className="text-foreground">Visitar web</span>
                    </a>
                  )}
                </div>

                <div className="space-y-3">
                  <Button variant="hero" size="lg" className="w-full">
                    Solicitar presupuesto
                  </Button>
                  <Button variant="romantic" size="lg" className="w-full gap-2">
                    <Heart className="w-5 h-5" />
                    Añadir a favoritos
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default VendorDetail;
